import { useCallback, useEffect, useState } from 'react';
import { GitBranch, Pencil, Plus, Route, Trash2 } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { Button, ConfirmationModal, EmptyState, Skeleton } from '../ui';
import { useMutation, useQuery } from '../gql/hooks';
import { useSubscription } from '../gql/useSubscription';
import {
  CLIENTS_QUERY,
  DELETE_ROUTING_POLICY_MUTATION,
  PROVIDER_HEALTH_CHANGED_SUBSCRIPTION,
  PROVIDERS_QUERY,
  ROUTING_POLICIES_QUERY,
  UPSERT_ROUTING_POLICY_MUTATION,
  type ClientsQueryResult,
  type DeleteRoutingPolicyMutationResult,
  type DeleteRoutingPolicyMutationVariables,
  type ProviderHealthChangedSubscriptionResult,
  type ProvidersQueryResult,
  type RoutingPoliciesQueryResult,
  type UpsertRoutingPolicyMutationResult,
  type UpsertRoutingPolicyMutationVariables,
} from '../gql/operations';
import type { Provider, RoutingPolicy, UpsertRoutingPolicyInput } from '../gql/types';
import { PageHeader } from '../features/shared/PageHeader';
import { StatusBadge } from '../features/shared/StatusBadge';
import { InlineBanner } from '../features/shared/InlineBanner';
import { FallbackChain } from '../features/policies/FallbackChain';
import { RouteSimulatorPanel } from '../features/policies/RouteSimulatorPanel';
import {
  RoutingPolicyFormModal,
  type ApplicationOption,
} from '../features/policies/RoutingPolicyFormModal';

// Routing policy editor (US-8.3.1 + W5). Lists every policy as a card showing its provider
// preference as a LIVE fallback chain (display name + health badge, joined from PROVIDERS_QUERY)
// + scope, and lets operators create/edit (modal form with drag + keyboard reorder) or delete
// (confirm-gated) policies. A "Test routing" simulator panel dry-runs the router for an (app,
// model) pair and renders the result as the same chain visualization.

function PolicyCard({
  policy,
  providers,
  onEdit,
  onDelete,
}: {
  policy: RoutingPolicy;
  providers: Provider[];
  onEdit: (policy: RoutingPolicy) => void;
  onDelete: (policy: RoutingPolicy) => void;
}) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const scopeBits: string[] = [];
  if (policy.scopeApplication) scopeBits.push(`app: ${policy.scopeApplication.name}`);
  if (policy.scopeCanonicalModel) scopeBits.push(`model: ${policy.scopeCanonicalModel}`);
  const scope = scopeBits.length > 0 ? scopeBits.join(' · ') : 'Global (all traffic)';

  // A disabled policy gets the same muted treatment as a disabled provider card: the whole
  // article fades to 60% opacity and the brand-gradient icon flips to a desaturated
  // slate→zinc fill (with the colored shadow removed) so it visually reads as inactive
  // rather than "indigo brand, just dimmer". Matches the ProviderHealthBoardPage pattern.
  const iconClasses = policy.enabled
    ? 'bg-gradient-to-br from-indigo-500 to-purple-600 text-white shadow-lg shadow-indigo-500/20'
    : isDark
      ? 'bg-gradient-to-br from-slate-700 to-slate-800 text-white/60'
      : 'bg-gradient-to-br from-slate-200 to-slate-300 text-slate-500';

  return (
    <article
      aria-label={policy.name}
      data-policy-enabled={policy.enabled ? 'true' : 'false'}
      className={`rounded-2xl border p-5 transition-all duration-300 ${
        policy.enabled ? '' : 'opacity-60'
      } ${
        isDark
          ? 'border-white/[0.06] bg-gradient-to-br from-white/[0.05] to-white/[0.02]'
          : 'border-slate-200 bg-white shadow-sm'
      }`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <span
            className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl ${iconClasses}`}
          >
            <GitBranch className="h-5 w-5" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h3
                className={`truncate text-base font-semibold ${
                  isDark ? 'text-white' : 'text-slate-900'
                }`}
              >
                {policy.name}
              </h3>
              <StatusBadge tone={policy.enabled ? 'active' : 'neutral'}>
                {policy.enabled ? 'ENABLED' : 'DISABLED'}
              </StatusBadge>
            </div>
            <p className={`mt-0.5 text-xs ${isDark ? 'text-white/40' : 'text-slate-500'}`}>
              {scope}
              {policy.capacityThreshold != null &&
                ` · spill at ${Math.round(policy.capacityThreshold * 100)}% capacity`}
            </p>
          </div>
        </div>

        <div className="flex flex-shrink-0 items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => onEdit(policy)}
            leftIcon={<Pencil className="h-4 w-4" aria-hidden="true" />}
            aria-label={`Edit ${policy.name}`}
          >
            Edit
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={() => onDelete(policy)}
            leftIcon={<Trash2 className="h-4 w-4" aria-hidden="true" />}
            aria-label={`Delete ${policy.name}`}
          >
            Delete
          </Button>
        </div>
      </div>

      <div className="mt-4">
        <span
          className={`mb-1.5 block text-[10px] font-medium uppercase tracking-widest ${
            isDark ? 'text-white/30' : 'text-slate-400'
          }`}
        >
          Routing order
        </span>
        <FallbackChain order={policy.providerOrder} providers={providers} />
      </div>
    </article>
  );
}

export function RoutingPolicyEditorPage() {
  const { data, loading, error, refetch } = useQuery<RoutingPoliciesQueryResult>(
    ROUTING_POLICIES_QUERY,
  );
  // Applications populate the optional scope picker; failure here is non-fatal (the picker just
  // offers "all applications").
  const clients = useQuery<ClientsQueryResult>(CLIENTS_QUERY);

  // Live provider fleet powers the fallback-chain health badges on each policy card + the simulator
  // panel's app picker. Lifted into local state so the providerHealthChanged subscription can upsert
  // health transitions in place (reusing the board's live-update pattern). Non-fatal on failure —
  // hops simply render without a resolved provider (ghost).
  const providersQuery = useQuery<ProvidersQueryResult>(PROVIDERS_QUERY);
  const [providers, setProviders] = useState<Provider[]>([]);
  useEffect(() => {
    if (providersQuery.data?.providers) setProviders(providersQuery.data.providers);
  }, [providersQuery.data]);

  const handleHealthChanged = useCallback((payload: ProviderHealthChangedSubscriptionResult) => {
    const changed = payload.providerHealthChanged;
    if (!changed) return;
    setProviders((prev) => {
      const idx = prev.findIndex((p) => p.id === changed.id);
      if (idx === -1) return [...prev, changed];
      const next = [...prev];
      next[idx] = changed;
      return next;
    });
  }, []);

  useSubscription<ProviderHealthChangedSubscriptionResult>(
    PROVIDER_HEALTH_CHANGED_SUBSCRIPTION,
    { onData: handleHealthChanged },
  );

  const upsert = useMutation<
    UpsertRoutingPolicyMutationResult,
    UpsertRoutingPolicyMutationVariables
  >(UPSERT_ROUTING_POLICY_MUTATION);
  const remove = useMutation<
    DeleteRoutingPolicyMutationResult,
    DeleteRoutingPolicyMutationVariables
  >(DELETE_ROUTING_POLICY_MUTATION);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<RoutingPolicy | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<RoutingPolicy | null>(null);

  const policies = data?.routingPolicies ?? [];

  const applications: ApplicationOption[] = (clients.data?.clients ?? []).flatMap((client) =>
    client.applications.map((app) => ({
      id: app.id,
      name: `${client.name} · ${app.name}`,
    })),
  );

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };
  const openEdit = (policy: RoutingPolicy) => {
    setEditing(policy);
    setFormOpen(true);
  };

  const handleSubmit = async (input: UpsertRoutingPolicyInput) => {
    try {
      await upsert.mutate({ input });
      setFormOpen(false);
      setEditing(null);
      refetch();
    } catch {
      // Error surfaced via upsert.error banner; keep the form open so the operator can retry.
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await remove.mutate({ id: deleteTarget.id });
      setDeleteTarget(null);
      refetch();
    } catch {
      // Error surfaced via remove.error banner; keep the confirm dialog open.
    }
  };

  const mutationError = upsert.error ?? remove.error ?? null;

  return (
    <div>
      <PageHeader
        title="Routing policies"
        description="Ordered provider preferences with optional scope and capacity-based failover."
        breadcrumbs={[{ label: 'Providers', to: '/providers' }, { label: 'Routing policies' }]}
        actions={
          <Button
            variant="primary"
            onClick={openCreate}
            leftIcon={<Plus className="h-4 w-4" aria-hidden="true" />}
          >
            New policy
          </Button>
        }
      />

      {error && (
        <InlineBanner tone="error">Couldn’t load policies: {error.message}</InlineBanner>
      )}

      {mutationError && (
        <InlineBanner tone="error" testId="mutation-error">
          That action couldn’t be completed: {mutationError.message}
        </InlineBanner>
      )}

      <RouteSimulatorPanel applications={applications} providers={providers} />

      {loading && (
        <div className="space-y-4" data-testid="policies-loading">
          {[0, 1].map((i) => (
            <Skeleton key={i} variant="block" height={140} />
          ))}
        </div>
      )}

      {!loading && !error && policies.length === 0 && (
        <EmptyState
          icon={<Route className="h-16 w-16" aria-hidden="true" />}
          title="No routing policies yet"
          description="Create a policy to control which providers handle traffic and in what order."
          action={{ label: 'Create first policy', onClick: openCreate }}
        />
      )}

      {!loading && policies.length > 0 && (
        <ul className="space-y-4">
          {policies.map((policy) => (
            <li key={policy.id}>
              <PolicyCard
                policy={policy}
                providers={providers}
                onEdit={openEdit}
                onDelete={setDeleteTarget}
              />
            </li>
          ))}
        </ul>
      )}

      <RoutingPolicyFormModal
        isOpen={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditing(null);
        }}
        policy={editing}
        applications={applications}
        loading={upsert.loading}
        onSubmit={(input) => void handleSubmit(input)}
      />

      <ConfirmationModal
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => void handleConfirmDelete()}
        variant="danger"
        title="Delete this policy?"
        confirmLabel="Delete policy"
        loading={remove.loading}
        description={
          <>
            Deleting <strong>{deleteTarget?.name}</strong> removes it from routing immediately.
            This cannot be undone.
          </>
        }
      />
    </div>
  );
}
